import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// web/src/pages/AgentsPage.tsx — The main financial operating system interface.
// 3-column layout: Agent Roster | Chat | Agent Details
// Luna is the default; specialists can be chatted with directly.
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { MessagesSquare } from "lucide-react";
import { useAutoResize } from "../hooks/useAutoResize";
import { AppShell } from "../components/layout/AppShell";
import { AgentRoster } from "../components/layout/AgentRoster";
import { AgentDetailsPanel } from "../components/layout/AgentDetailsPanel";
import { AgentMessage } from "../components/chat/AgentMessage";
// PipelineSteps no longer rendered directly by AgentsPage — see v2.6 note.
import { Badge } from "../components/ui/Badge";
import { useAgentsStore } from "../stores/agentsStore";
import { useConversationStore } from "../stores/conversationStore";
import { agentLabel } from "../lib/agentConfig";
import { useArtifactsStore } from "../stores/artifactsStore";
import { useAuthStore } from "../stores/authStore";
import { api, apiRequest } from "../lib/apiClient";
import { StarterCards } from "../components/chat/StarterCards";
import { useCommandPaletteStore } from "../stores/commandPaletteStore";
const POLL_INTERVAL_MS = 1500;
// ── Main page ───────────────────────────────────────────────────────────────
export default function AgentsPage() {
    const selectedAgentId = useAgentsStore((s) => s.selectedAgentId);
    const agentMode = useAgentsStore((s) => s.agentMode);
    const loadSystemAgents = useAgentsStore((s) => s.loadSystemAgents);
    const loadCustomAgents = useAgentsStore((s) => s.loadCustomAgents);
    const loadHistory = useConversationStore((s) => s.loadHistory);
    const loadArtifacts = useArtifactsStore((s) => s.loadArtifacts);
    const accessToken = useAuthStore((s) => s.accessToken);
    const thread = useConversationStore((s) => selectedAgentId === "luna"
        ? s.getActiveLunaThread()
        : (s.threads[selectedAgentId] ?? s.getThread(selectedAgentId)));
    const addUserMessage = useConversationStore((s) => s.addUserMessage);
    const addPendingAssistantMessage = useConversationStore((s) => s.addPendingAssistantMessage);
    const addAssistantMessage = useConversationStore((s) => s.addAssistantMessage);
    const setTyping = useConversationStore((s) => s.setTyping);
    const setTaskId = useConversationStore((s) => s.setTaskId);
    const setConversationId = useConversationStore((s) => s.setConversationId);
    const [input, setInput] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const textareaRef = useRef(null);
    const openPalette = useCommandPaletteStore((s) => s.openPalette);
    const pendingChatPrompt = useCommandPaletteStore((s) => s.pendingChatPrompt);
    const consumePendingChatPrompt = useCommandPaletteStore((s) => s.consumePendingChatPrompt);
    // Ref guard prevents double-submit races — React state is async, so two
    // rapid submissions (Enter + button, double-Enter) can both pass the
    // isSubmitting check before the first setIsSubmitting(true) re-renders.
    const isSubmittingRef = useRef(false);
    const messagesEndRef = useRef(null);
    // pollRef maps task_id → timeout handle so concurrent tasks can't
    // cross-cancel each other's polls. (The old single-ref pattern meant
    // task B's stopPolling() would clear task A's timeout, leaving A running
    // untracked and calling addAssistantMessage twice.)
    const pollTimers = useRef({});
    const hasLoadedRef = useRef(false);
    const handleTextareaResize = useAutoResize(160);
    // Load roster + history once per mount, as soon as the token is available.
    // Using a ref guard prevents re-firing on every token refresh (the WS hook
    // calls auth.refresh() on 1008 close, updating accessToken each time —
    // without the guard, each refresh would retrigger 3 API calls in a loop).
    useEffect(() => {
        if (accessToken && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            void loadSystemAgents();
            void loadCustomAgents();
            void loadHistory();
            void loadArtifacts();
        }
        // All are stable Zustand actions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);
    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [thread.messages, thread.isTyping]);
    // Pickup: subscribe to the command-palette store's pendingChatPrompt.
    // When the global ⌘K / TopBar "What-if Analysis" picker selects a
    // capability, the modal stages the prompt in the store and navigates to
    // /. This effect fires on any change to pendingChatPrompt, so it works
    // whether the user is arriving from another route OR is already on /
    // (where navigate("/") is a no-op and a mount-time effect would never
    // re-run). Match the in-page palette button: prefill the input and
    // focus, do not auto-send.
    useEffect(() => {
        if (!pendingChatPrompt)
            return;
        setInput(pendingChatPrompt);
        consumePendingChatPrompt();
        setTimeout(() => textareaRef.current?.focus(), 0);
    }, [pendingChatPrompt, consumePendingChatPrompt]);
    // Auto-grow the composer to fit its content whenever `input` changes. The
    // onInput handler only fires on user typing, so a PROGRAMMATIC fill (e.g. the
    // X-Ray "Discuss with Luna" seed) would otherwise stay one line tall and show
    // only its last line. This makes the injected context visible (and scrollable
    // up to the max height) so the user can read and edit it before sending.
    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el)
            return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [input]);
    const currentThread = thread;
    function stopPolling(taskId) {
        if (taskId) {
            clearTimeout(pollTimers.current[taskId]);
            delete pollTimers.current[taskId];
        }
        else {
            // Clear all outstanding polls (used on unmount)
            Object.values(pollTimers.current).forEach(clearTimeout);
            pollTimers.current = {};
        }
    }
    async function pollResult(taskId, agentId) {
        try {
            const status = await apiRequest(`/execute/${taskId}`);
            if (status.status === "queued" || status.status === "running") {
                // Still in progress — reschedule this specific task's timer.
                pollTimers.current[taskId] = setTimeout(() => void pollResult(taskId, agentId), POLL_INTERVAL_MS);
                return;
            }
            // Fetch final result
            const result = await apiRequest(`/execute/${taskId}/result`);
            stopPolling(taskId);
            if (result.status === "failed") {
                addAssistantMessage(agentId, {
                    role: "assistant",
                    content: result.error ?? "Something went wrong processing this request. Please try again.",
                    agent_id: agentId,
                    // Pass taskId so the in-flight placeholder gets replaced in-place
                    // rather than appended below the spinner.
                    task_id: taskId,
                });
            }
            else {
                // Convert trace_steps to PipelineStep format — preserve LLM detail fields
                const pipelineSteps = (result.trace_steps ?? []).map((s) => ({
                    step: s.step,
                    agent: s.agent,
                    status: "done",
                    llm_input: s.llm_input,
                    llm_output: s.llm_output,
                    latency_ms: s.latency_ms,
                    wall_ms: s.wall_ms,
                }));
                addAssistantMessage(agentId, {
                    role: "assistant",
                    content: result.reply ?? "(no reply)",
                    agent_id: agentId,
                    task_id: taskId,
                    sub_role: result.sub_role,
                    confidence: result.confidence,
                    reasoning_trace_id: result.reasoning_trace_id,
                    langfuse_trace_url: result.langfuse_trace_url,
                    action_cards: result.action_cards?.map((c) => ({
                        id: c.id,
                        title: c.title,
                        description: c.description,
                        action_type: c.action_type,
                        priority: c.priority,
                        data: c.data,
                    })),
                    interactive_card: result.interactive_card,
                    renders: result.renders,
                    specialists: result.specialists,
                    // v2.4 analyst-report fields. Without these the AnalystReport
                    // template only renders the Recommendations section.
                    response_template: result.response_template,
                    executive_summary: result.executive_summary,
                    headline_metrics: result.headline_metrics,
                    analysis_narrative: result.analysis_narrative,
                    inline_renders: result.inline_renders,
                    // v2.8 institutional-memo fields.
                    executive_situation: result.executive_situation,
                    executive_recommendation: result.executive_recommendation,
                    situation_narrative: result.situation_narrative,
                    balance_sheet: result.balance_sheet,
                    cash_flow: result.cash_flow,
                    assumptions: result.assumptions,
                    assumptions_used: result.assumptions_used,
                    risks: result.risks,
                    open_items: result.open_items,
                    // CR-2026-05-22-001: reconciled cross-specialist recs.
                    unified_recommendations: result.unified_recommendations,
                    pipeline_steps: pipelineSteps.length > 0 ? pipelineSteps : undefined,
                    suggested_prompts: result.suggested_prompts,
                });
            }
        }
        catch (e) {
            stopPolling(taskId);
            addAssistantMessage(agentId, {
                role: "assistant",
                content: `Request failed: ${String(e)}`,
                agent_id: agentId,
            });
        }
        // Cleanup runs only for terminal outcomes (success, failed, error).
        // The early return above for queued/running skips this block intentionally.
        // Only clear isSubmitting when no other polls are in flight.
        setTaskId(agentId, null);
        if (Object.keys(pollTimers.current).length === 0) {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
        setTyping(agentId, false);
    }
    async function submitMessage(msg) {
        if (!msg || isSubmittingRef.current)
            return;
        isSubmittingRef.current = true;
        setInput("");
        setIsSubmitting(true);
        addUserMessage(selectedAgentId, msg);
        setTyping(selectedAgentId, true);
        try {
            const body = { user_message: msg };
            // Conversation continuity
            if (currentThread.conversationId) {
                body.conversation_id = currentThread.conversationId;
            }
            body.conversation_turn = currentThread.turnNumber;
            // Direct routing — skip Orchestrator if not Luna
            if (selectedAgentId !== "luna") {
                body.target_agent = selectedAgentId;
            }
            const res = await apiRequest("/execute", {
                method: "POST",
                body,
            });
            setTaskId(selectedAgentId, res.task_id);
            // Persist conversation ID so subsequent turns reuse the same conversation.
            // This enables context scoping: load_conversation_messages instead of
            // cross-conversation load_recent_messages on the next turn.
            if (res.conversation_id) {
                setConversationId(selectedAgentId, res.conversation_id);
            }
            // Add a pending assistant placeholder bound to this task_id. Pipeline
            // events arriving on the WS will attach their steps to THIS message
            // (matched by task_id), so the user sees live step-by-step progress
            // instead of a final all-done snapshot at the end. When the final
            // response lands, addAssistantMessage will splice over this placeholder.
            addPendingAssistantMessage(selectedAgentId, res.task_id);
            // Start polling — keyed by task_id so concurrent tasks don't cancel each other.
            pollTimers.current[res.task_id] = setTimeout(() => void pollResult(res.task_id, selectedAgentId), POLL_INTERVAL_MS);
        }
        catch (e) {
            setTyping(selectedAgentId, false);
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            addAssistantMessage(selectedAgentId, {
                role: "assistant",
                content: `Failed to send: ${String(e)}`,
                agent_id: selectedAgentId,
            });
        }
    }
    function handleSubmit(e) {
        e.preventDefault();
        void submitMessage(input.trim());
    }
    // Resume polling for any in-flight task when the agent tab is switched to or
    // the page remounts. thread.taskId survives navigation (it's in Zustand memory)
    // but pollTimers is a component ref that gets cleared on unmount. Without this
    // effect the task keeps running on the server but the UI stops updating.
    useEffect(() => {
        const taskId = thread.taskId;
        if (!taskId || pollTimers.current[taskId])
            return;
        setIsSubmitting(true);
        isSubmittingRef.current = true;
        setTyping(selectedAgentId, true);
        pollTimers.current[taskId] = setTimeout(() => void pollResult(taskId, selectedAgentId), POLL_INTERVAL_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgentId]);
    // Cleanup on unmount — cancel timers but leave taskId in store so the
    // resume effect above can pick it up when the user navigates back.
    useEffect(() => () => {
        Object.values(pollTimers.current).forEach(clearTimeout);
        pollTimers.current = {};
    }, []);
    async function handleStop() {
        const taskId = currentThread.taskId;
        Object.keys(pollTimers.current).forEach((tid) => stopPolling(tid));
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        setTyping(selectedAgentId, false);
        setTaskId(selectedAgentId, null);
        if (taskId) {
            await api.executeStop(taskId).catch(() => { });
        }
    }
    // Open the global command palette modal, routing the chosen prompt back
    // into THIS chat input (so the user can edit before sending).
    function openPaletteForInput() {
        openPalette((prompt) => {
            setInput(prompt);
            setTimeout(() => textareaRef.current?.focus(), 0);
        });
    }
    // Mode badge
    const modeBadgeVariant = agentMode === "orchestrated" ? "orchestrated" :
        agentMode === "custom" ? "custom" :
            "direct";
    const modeBadgeLabel = agentMode === "orchestrated" ? "Orchestrated" :
        agentMode === "custom" ? "Custom" :
            "Direct";
    const agentDisplayName = agentLabel(selectedAgentId);
    return (_jsx(AppShell, { children: _jsxs("div", { className: "flex h-full overflow-hidden relative", children: [_jsx(AgentRoster, { mobileOpen: sidebarOpen, onMobileClose: () => setSidebarOpen(false) }), _jsxs("div", { className: "flex-1 flex flex-col min-w-0 bg-slate-950", children: [_jsxs("div", { className: "h-11 flex items-center gap-2 px-3 md:gap-3 md:px-5 border-b border-slate-800/60 shrink-0", children: [_jsxs("button", { type: "button", onClick: () => setSidebarOpen(true), className: "md:hidden inline-flex items-center gap-1.5 h-9 px-2 -ml-1 rounded-md text-slate-300 bg-slate-800/70 hover:bg-slate-700 hover:text-slate-100 border border-slate-700/70 transition-colors", "aria-label": "Open conversations", title: "Conversations", children: [_jsx(MessagesSquare, { size: 16 }), _jsx("span", { className: "text-xs font-medium", children: "Chats" })] }), _jsx("span", { className: "text-sm font-medium text-slate-300 truncate", children: agentDisplayName }), _jsx(Badge, { variant: modeBadgeVariant, size: "xs", children: modeBadgeLabel }), agentMode !== "orchestrated" && (_jsx("span", { className: "hidden md:inline text-[10px] text-slate-600 ml-1", children: "Your preferences are being learned from this conversation." }))] }), _jsxs("div", { className: "flex-1 overflow-y-auto px-3 py-4 md:px-5 space-y-5", children: [currentThread.messages.length === 0 && (_jsxs("div", { className: "flex flex-col items-center justify-center h-full text-center py-16 space-y-4", children: [_jsx("span", { className: "text-4xl text-slate-800", children: selectedAgentId === "luna" ? "✦" : "◆" }), selectedAgentId === "luna" ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-sm text-slate-600 max-w-xs", children: "What would you like to work on?" }), _jsx(StarterCards, { onSelect: (prompt) => void submitMessage(prompt) })] })) : (_jsx("p", { className: "text-sm text-slate-600 max-w-xs", children: `Start a conversation with ${agentDisplayName}.` }))] })), currentThread.messages.map((msg) => (_jsx(AgentMessage, { message: msg, onApproveCard: async (_cardId) => {
                                        // TODO: call approval API
                                    }, onRejectCard: async (_cardId) => {
                                        // TODO: call reject API
                                    }, onAskLunaAboutCard: (_cardId, title) => {
                                        setInput(`Explain this recommendation: ${title}`);
                                    }, onSuggestionSelect: (prompt) => void submitMessage(prompt) }, msg.id))), _jsx("div", { ref: messagesEndRef })] }), _jsx("div", { className: "relative shrink-0", children: _jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "px-3 py-3 md:px-5 md:py-4 border-t border-slate-800/60", children: [_jsxs("div", { className: "flex items-end gap-3 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 focus-within:border-slate-700 transition-colors", children: [selectedAgentId === "luna" && !isSubmitting && (_jsx("button", { type: "button", className: "cmd-hint", onClick: openPaletteForInput, tabIndex: -1, "aria-label": "Open command palette", children: "/" })), _jsx("textarea", { value: input, onChange: (e) => {
                                                    const v = e.target.value;
                                                    // Typing "/" on its own opens the global palette and routes
                                                    // selection back into this input. Don't keep the "/" character.
                                                    if (v === "/" && selectedAgentId === "luna") {
                                                        openPaletteForInput();
                                                        return;
                                                    }
                                                    setInput(v);
                                                }, onKeyDown: (e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        void handleSubmit(e);
                                                    }
                                                }, placeholder: selectedAgentId === "luna"
                                                    ? "Ask Luna anything…"
                                                    : `Message ${agentDisplayName}…`, ref: textareaRef, rows: 1, className: "flex-1 bg-transparent resize-none text-sm text-slate-200 placeholder-slate-600 focus:outline-none min-h-[24px] max-h-40 overflow-y-auto", onInput: handleTextareaResize, disabled: isSubmitting }), isSubmitting ? (_jsx("button", { type: "button", onClick: () => void handleStop(), className: "shrink-0 p-1.5 text-red-400 hover:text-red-300 transition-colors", "aria-label": "Stop", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 18 18", fill: "currentColor", children: _jsx("rect", { x: "4", y: "4", width: "10", height: "10", rx: "1.5" }) }) })) : (_jsx("button", { type: "submit", disabled: !input.trim(), className: "shrink-0 p-1.5 text-violet-400 hover:text-violet-300 disabled:text-slate-700 disabled:cursor-not-allowed transition-colors", "aria-label": "Send message", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "22", y1: "2", x2: "11", y2: "13" }), _jsx("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })] }) }))] }), _jsx("p", { className: "mt-1.5 text-[10px] text-slate-700 text-center", children: "Enter to send \u00B7 Shift+Enter for new line" })] }) })] }), selectedAgentId !== "luna" && (_jsx("div", { className: "hidden lg:flex", children: _jsx(AgentDetailsPanel, {}) }))] }) }));
}
